import React from 'react';
import { Flag, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';

export function AddGoalDialog({ goalDialogOpen, setGoalDialogOpen, newGoal, setNewGoal, createGoal }) {
  return (
    <Dialog open={goalDialogOpen} onOpenChange={setGoalDialogOpen}>
      <DialogContent data-testid="add-goal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-amber-500" />
            Set Benchmark Goal
          </DialogTitle>
          <DialogDescription>
            Set a target percentile rank to track your improvement over time
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Goal Type</Label>
            <Select 
              value={newGoal.metric_id || 'overall'} 
              onValueChange={(v) => setNewGoal(g => ({ ...g, metric_id: v === 'overall' ? null : v }))}
            >
              <SelectTrigger data-testid="goal-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="overall">Overall Percentile</SelectItem>
                <SelectItem value="productivity">Productivity</SelectItem>
                <SelectItem value="employee_engagement">Employee Engagement</SelectItem>
                <SelectItem value="quality">Quality</SelectItem>
                <SelectItem value="customer_satisfaction">Customer Satisfaction</SelectItem>
                <SelectItem value="roi_percent">ROI Percentage</SelectItem>
                <SelectItem value="training_performance">Training Performance</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Target Percentile</Label>
            <div className="flex items-center gap-4">
              <Input
                type="number"
                value={newGoal.target_percentile}
                onChange={(e) => setNewGoal(g => ({ ...g, target_percentile: Math.min(99, Math.max(1, parseInt(e.target.value) || 50)) }))}
                min={1}
                max={99}
                className="w-24"
                data-testid="goal-target-input"
              />
              <span className="text-slate-500">th percentile</span>
            </div>
            <div className="flex gap-2 mt-2">
              {[50, 75, 90].map((p) => (
                <Button
                  key={p}
                  variant="outline"
                  size="sm"
                  onClick={() => setNewGoal(g => ({ ...g, target_percentile: p }))}
                  className={newGoal.target_percentile === p ? 'border-indigo-500 bg-indigo-50' : ''}
                >
                  {p}th
                </Button>
              ))}
            </div>
          </div>
          
          {/* Notification Settings */}
          <div className="space-y-4 p-4 bg-slate-50 rounded-lg border">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-slate-600" />
              <Label className="font-medium">Email Notifications</Label>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Enable Notifications</p>
                <p className="text-xs text-slate-500">Receive emails for goal updates</p>
              </div>
              <Switch
                checked={newGoal.email_notifications}
                onCheckedChange={(checked) => setNewGoal(g => ({ ...g, email_notifications: checked }))}
                data-testid="email-notifications-toggle"
              />
            </div>
            
            {newGoal.email_notifications && (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Milestone Alerts</p>
                    <p className="text-xs text-slate-500">Notify at 25%, 50%, 75% progress</p>
                  </div>
                  <Switch
                    checked={newGoal.milestone_alerts}
                    onCheckedChange={(checked) => setNewGoal(g => ({ ...g, milestone_alerts: checked }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Drop Alert Threshold</p>
                      <p className="text-xs text-slate-500">Alert when rank drops by this amount</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={newGoal.drop_alert_threshold}
                      onChange={(e) => setNewGoal(g => ({ ...g, drop_alert_threshold: Math.min(50, Math.max(1, parseInt(e.target.value) || 10)) }))}
                      min={1}
                      max={50}
                      className="w-20"
                    />
                    <span className="text-sm text-slate-500">percentile drop</span>
                  </div>
                </div>
              </>
            )}
          </div>
          
          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-800">
              <strong>Tip:</strong> The 75th percentile means you outperform 75% of organizations in your industry.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setGoalDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={createGoal}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="create-goal-submit"
          >
            <Flag className="h-4 w-4 mr-2" />
            Set Goal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
